<template>
    <q-select filled v-model="company" use-input input-debounce="0" :label="label" :hint="hint" :options="companies"
        @filter="filterCompany" @update:modelValue="selectCompany" :dense="true" :clearable="true" :loading="loading"
        :readonly="readonly">
        <template v-slot:no-option>
            <q-item>
                <q-item-section class="text-grey">
                    Sin resultados
                </q-item-section>
            </q-item>
        </template>
    </q-select>
</template>


<script lang="ts">
import { defineComponent, onMounted, reactive, toRefs, watch } from 'vue';
import { useQuasar } from 'quasar';

import { FindCompanyAutocompleteDto, OptionCompanyDto, OptionDto } from '@/dtos';
import companyService from '@/services/fleet-management/company.service';
import { errorNotify } from '@/helpers/utilities';


interface Props {
    clear?: boolean;
    label?: string;
    hint?: string;
    readonly?: boolean;
    company_id?: number;
    city_id?: number;
}

export default defineComponent({
    name: 'CompanyAutoComplete',
    props: {
        clear: { type: Boolean },
        label: { type: String },
        hint: { type: String },
        readonly: { type: Boolean, default: false },
        company_id: { type: Number },
        city_id: { type: Number }
    },
    emits: ['company_selected', 'update:company_id'],
    setup(props: Props, { emit }) {
        const $q = useQuasar();

        const data = reactive({
            company: '',
            companies: [] as OptionDto[],
            city_id: props.city_id,
            company_id: props.company_id
        });

        const config = reactive({
            loading: false,
            label: props.label ?? 'Empresa',
            hint: props.hint,
            searcher: '',
        });

        const fetchCompanies = async () => {
            try {
                config.loading = true;

                const params: FindCompanyAutocompleteDto = {
                    searcher: config.searcher,
                    city_id: data.city_id
                }
                const companies = await companyService.findAllAutocomplete(params);
                data.companies = [];
                for (const company of companies.data) {
                    const opt: OptionCompanyDto = {
                        label: company.name,
                        value: company.id,
                        address: company.address,
                        latitude: company.latitude,
                        longitude: company.longitude
                    }
                    data.companies.push(opt);
                }

                if (data.company_id && data.company_id != 0) {
                    const optSelected = data.companies.find(x => x.value == data.company_id);
                    if (optSelected) {
                        data.company = optSelected.label;
                        config.loading = false;
                        return;
                    }

                    await fetchCompany(data.company_id);
                }

                config.loading = false;
            } catch (error) {
                console.log(error);
                $q.notify(errorNotify('No se pudo obtener la información de la empresa'));
            }
        }

        const fetchCompany = async (company_id: number) => {
            try {
                const companyResult = await companyService.findById(company_id);
                if (companyResult.data) {
                    const opt: OptionCompanyDto = {
                        label: companyResult.data.name,
                        value: companyResult.data.id,
                        address: companyResult.data.address,
                        latitude: companyResult.data.latitude,
                        longitude: companyResult.data.longitude
                    }

                    data.companies.push(opt);
                    data.company = opt.label;
                }
            } catch (error) {
                console.log(error);
                $q.notify(errorNotify('No se pudo obtener la información de la empresa.'));
            }
        }

        const selectCompany = (option: OptionDto) => {
            emit('company_selected', option);
        }

        const filterCompany = async (val: string, update: (fn: () => void) => void) => {
            if (val == '') {
                update(async () => {
                    config.searcher = '';
                    await fetchCompanies();
                })
                return;
            }

            update(async () => {
                const needle = val.toLowerCase();
                if (needle.length > 2) {
                    config.searcher = needle;
                    data.company_id = undefined;
                    await fetchCompanies();
                }
            })
        }

        onMounted(async () => {
            await fetchCompanies();
        });

        watch([() => props.clear, () => props.company_id, () => props.city_id], async ([
            newClear, newCompanyId, newCityId], [oldClear, oldCompanyId, oldCityId]) => {
            if (newClear != oldClear || !newCompanyId) {
                data.company = '';
                data.company_id = undefined;

                config.searcher = '';
                if (newCityId && newCityId > 0 && newCityId != oldCityId) {
                    data.city_id = newCityId;
                } else {
                    data.city_id = undefined;
                }

                await fetchCompanies();
                return;
            }

            if (newCompanyId && newCompanyId > 0) {
                data.company_id = newCompanyId;
                await fetchCompanies();
                return;
            }

            if (newCityId && newCityId > 0 && newCityId != oldCityId) {
                data.company_id = undefined;
                data.company = '';
            }

            data.city_id = newCityId;
            await fetchCompanies();
        });

        return {
            ...toRefs(data),
            ...toRefs(config),
            filterCompany,
            selectCompany
        }
    }
});
</script>
